import { Node, mergeAttributes } from "@tiptap/core";

/**
 * The two nodes a long-form article needs that StarterKit does not ship.
 *
 * WHY THESE ARE HAND-WRITTEN rather than pulled from `@tiptap/extension-image` and friends: their attribute
 * sets have to mirror the server sanitizer EXACTLY. Anything the editor can produce that the sanitizer does
 * not allow is stripped on save without warning — an author writes a guide with screenshots and gets back
 * plain paragraphs. Owning both ends means the two lists cannot drift.
 */

/** Mirrors `BlogCalloutTone` in shared-types and `ALLOWED_CALLOUT_TONES` in the sanitizer. */
export const RICH_TEXT_CALLOUT_TONES = ["note", "warning", "tip"] as const;
export type RichTextCalloutTone = (typeof RICH_TEXT_CALLOUT_TONES)[number];

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    callout: {
      /** Wrap the selection in a callout, or change the tone of the one it is already in. */
      setCallout: (tone: RichTextCalloutTone) => ReturnType;
      toggleCallout: (tone: RichTextCalloutTone) => ReturnType;
      unsetCallout: () => ReturnType;
    };
    longFormImage: {
      setLongFormImage: (attrs: { src: string; alt?: string }) => ReturnType;
    };
  }
}

/**
 * `<aside data-callout="note|warning|tip">`.
 *
 * The tone is an attribute from a fixed set rather than a class, so the sanitizer has a list to check against
 * instead of passing author-controlled class strings to the page.
 */
export const CalloutNode = Node.create({
  name: "callout",
  group: "block",
  content: "block+",
  defining: true,

  addAttributes() {
    return {
      tone: {
        default: "note" satisfies RichTextCalloutTone,
        parseHTML: (element) => {
          const value = element.getAttribute("data-callout");
          return RICH_TEXT_CALLOUT_TONES.includes(value as RichTextCalloutTone) ? value : "note";
        },
        renderHTML: (attributes) => ({ "data-callout": attributes.tone }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "aside[data-callout]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["aside", mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setCallout:
        (tone) =>
        ({ commands }) =>
          commands.wrapIn(this.name, { tone }),
      toggleCallout:
        (tone) =>
        ({ commands }) =>
          commands.toggleWrap(this.name, { tone }),
      unsetCallout:
        () =>
        ({ commands }) =>
          commands.lift(this.name),
    };
  },
});

/**
 * `<img src alt width height>`, and nothing else.
 *
 * `alt` always renders, empty if the author gave none: an `<img>` with no alt attribute at all is a
 * screen-reader problem, whereas an explicitly empty one reads as decorative. Same rule as the sanitizer.
 */
export const LongFormImageNode = Node.create({
  name: "longFormImage",
  group: "block",
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: "" },
      width: { default: null },
      height: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: "img[src]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["img", mergeAttributes(HTMLAttributes, { alt: HTMLAttributes.alt ?? "" })];
  },

  addCommands() {
    return {
      setLongFormImage:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: { alt: "", ...attrs } }),
    };
  },
});
